import { joinList } from '../lib/format';
import { APP_NAME, platform } from '../lib/platform';
import { usePasswordSignIn, useProviders } from '../lib/serverInfo';

/** The three things a new host needs to know, used on sign-in, the welcome sheet and the help page. */
export function HostSteps() {
  return (
    <ol className="steps">
      <li>
        <span>
          <b>Start or schedule.</b> “New meeting” starts a call now. “Get link” creates a link you can send in advance.
        </span>
      </li>
      <li>
        <span>
          <b>Share the link.</b> The people you invite don’t need an account. They open the link, type their name and ask to join.
        </span>
      </li>
      <li>
        <span>
          <b>Admit who you want.</b> Each request shows up on your screen with Admit and Deny. Nobody sees or hears the meeting until you let them in.
        </span>
      </li>
    </ol>
  );
}

/** How signing in works, naming only the ways to sign in that this server offers. */
export function SignInFacts() {
  const providers = useProviders().providers ?? [];
  const names = providers.map((p) => p.label);
  const password = usePasswordSignIn() === true;
  // In the iPhone and iPad app, Apple asks in its own sign-in window over the app (SignIn.tsx), not on a page.
  const sheet = platform === 'ios' && providers.some((p) => p.id === 'apple' && p.iosNative);
  const onPages = names.filter((n) => !(sheet && n === 'Apple'));
  const where = [onPages.length > 0 ? `on ${joinList(onPages.map((n) => `${n}’s`))} own page` : null, sheet ? 'in Apple’s own sign-in window' : null].filter(Boolean).join(' or ');
  // Hide My Email: Apple's private address is not the person's own, so it never opens the same account as their own address.
  const apple = names.includes('Apple') ? (
    <li>
      With Apple you can hide your email address. Apple then gives us a private address that forwards to your own inbox, and that address opens its own {APP_NAME} account, separate from one you made another way.
    </li>
  ) : null;
  if (password) {
    return (
      <div className="prose">
        <ul>
          <li>
            {names.length > 0
              ? `Sign in with the ${joinList(names)} account you already have, ${where}, or with your email address and a ${APP_NAME} password.`
              : `Sign in with your email address and your ${APP_NAME} password.`}
          </li>
          {names.length > 0 ? (
            <li>With {joinList(names)} we receive your name and email address, nothing else. We never see that password and cannot read your mail, files or calendar.</li>
          ) : null}
          {apple}
          <li>If you choose a password, we keep only a scrambled form of it, which can’t be turned back into the password.</li>
          <li>An account made with an email address is created once you confirm the address, with the link we email you. {names.length > 0 ? `Signing in with ${joinList(names)} for the first time creates your account straight away.` : ''}</li>
          {names.length > 0 ? (
            <li>
              Every way you sign in opens the same {APP_NAME} account when it uses the same email address: {names.join(', ')} or your email address and password. We email you when a sign-in method is added.
            </li>
          ) : null}
          <li>You stay signed in on a device for 30 days after you last used it. Settings lists your signed-in devices and lets you sign any of them out, or all the others at once.</li>
          <li>Only hosts need to sign in. Guests join from a link with just their name.</li>
        </ul>
      </div>
    );
  }
  return (
    <div className="prose">
      <ul>
        <li>
          There is no {APP_NAME} password.{' '}
          {names.length > 0
            ? `You always sign in with the ${joinList(names)} account you already have, ${where}.`
            : 'You always sign in with an account you already have, on its own sign-in page.'}
        </li>
        <li>We receive your name and email address, nothing else. We never see your password and cannot read your mail, files or calendar.</li>
        {apple}
        <li>The first time you sign in, your {APP_NAME} account is created automatically and we email you a short welcome guide.</li>
        {names.length > 1 ? (
          <li>
            If your {joinList(names, 'and')} accounts use the same verified email address, {names.length > 2 ? 'each opens' : 'both open'} the same {APP_NAME} account. We email you when a second sign-in method is added.
          </li>
        ) : null}
        <li>You stay signed in on a device for 30 days after you last used it. Settings lists your signed-in devices and lets you sign any of them out, or all the others at once.</li>
        <li>Only hosts need to sign in. Guests join from a link with just their name.</li>
      </ul>
    </div>
  );
}

export function PermissionFacts() {
  return (
    <div className="prose">
      <ul>
        <li>The first time you join, your browser or phone asks to use the camera and microphone. Choose Allow to be seen and heard.</li>
        <li>If you block the camera you can still join with audio only. If you block both, you can join and listen.</li>
        <li>You can change your mind at any time: the join screen tells you exactly where to switch access back on.</li>
        <li>Your camera and microphone are never shared while you are waiting to be admitted.</li>
      </ul>
    </div>
  );
}
